import { useState, useMemo } from 'preact/hooks';
import { route } from 'preact-router';
import DOMPurify from 'dompurify';
import { categories, faqs } from '../lib/help-content';

export function HelpPage({ user, workspace, onRestartTour }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);

  // Check if user is admin/owner/approver
  const isAdmin = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'approver';

  // Filter categories and articles based on role and search
  const filteredCategories = useMemo(() => {
    // First filter by role
    let roleFiltered = categories
      .filter(cat => isAdmin || !cat.adminOnly)
      .map(cat => ({
        ...cat,
        articles: cat.articles.filter(article => isAdmin || !article.adminOnly)
      }))
      .filter(cat => cat.articles.length > 0);

    // Then filter by search
    if (!searchQuery.trim()) return roleFiltered;

    const query = searchQuery.toLowerCase();
    return roleFiltered.map(cat => ({
      ...cat,
      articles: cat.articles.filter(
        article =>
          article.title.toLowerCase().includes(query) ||
          article.summary.toLowerCase().includes(query) ||
          article.content.toLowerCase().includes(query)
      )
    })).filter(cat => cat.articles.length > 0);
  }, [searchQuery, isAdmin]);

  // Filter FAQs based on role and search
  const filteredFaqs = useMemo(() => {
    // First filter by role
    let roleFiltered = faqs.filter(faq => isAdmin || !faq.adminOnly);

    // Then filter by search
    if (!searchQuery.trim()) return roleFiltered;

    const query = searchQuery.toLowerCase();
    return roleFiltered.filter(
      faq =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
    );
  }, [searchQuery, isAdmin]);

  return (
    <div class="help-page">
      <div class="bento-page-header-row">
        <div>
          <h1 class="bento-page-title">Help Center</h1>
          <p class="bento-page-subtitle">Find answers and learn how to use Unplug</p>
        </div>
        {onRestartTour && (
          <button class="btn btn-secondary" onClick={onRestartTour}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.5rem' }}>
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            Restart Tour
          </button>
        )}
      </div>

      {/* Search */}
      <div class="bento-card help-search-card">
        <div class="help-search-wrapper">
          <svg class="help-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            placeholder="Search help articles, FAQs..."
            value={searchQuery}
            onInput={(e) => setSearchQuery(e.target.value)}
            class="help-search-input"
          />
          {searchQuery && (
            <button class="help-search-clear" onClick={() => setSearchQuery('')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div class="help-quick-links">
        <a href="#getting-started" class="quick-link-card">
          <span class="quick-link-icon">🚀</span>
          <span class="quick-link-label">Getting Started</span>
        </a>
        <a href="#faqs" class="quick-link-card">
          <span class="quick-link-icon">❓</span>
          <span class="quick-link-label">FAQs</span>
        </a>
        <button class="quick-link-card" onClick={() => {
          // Find and click the support button
          const supportBtn = document.querySelector('.support-fab');
          if (supportBtn) supportBtn.click();
        }}>
          <span class="quick-link-icon">💬</span>
          <span class="quick-link-label">Contact Support</span>
        </button>
        {onRestartTour && (
          <button class="quick-link-card" onClick={onRestartTour}>
            <span class="quick-link-icon">🎯</span>
            <span class="quick-link-label">Take the Tour</span>
          </button>
        )}
      </div>

      {/* Search Results Summary */}
      {searchQuery && (
        <div class="help-search-results">
          Found {filteredCategories.reduce((acc, cat) => acc + cat.articles.length, 0)} articles
          and {filteredFaqs.length} FAQs matching "{searchQuery}"
        </div>
      )}

      {/* Categories */}
      <div class="help-categories">
        {filteredCategories.map(category => (
          <div key={category.id} id={category.id} class="bento-card help-category">
            <h3 class="help-category-title">
              <span class="help-category-icon">{category.icon}</span>
              {category.title}
            </h3>
            <ul class="help-article-list">
              {category.articles.map(article => (
                <li key={article.id} class="help-article-item">
                  <button
                    class={`help-article-header ${expandedArticle === article.id ? 'expanded' : ''}`}
                    onClick={() => setExpandedArticle(expandedArticle === article.id ? null : article.id)}
                  >
                    <div class="help-article-info">
                      <span class="help-article-title">{article.title}</span>
                      <span class="help-article-summary">{article.summary}</span>
                    </div>
                    <svg
                      class={`help-article-chevron ${expandedArticle === article.id ? 'expanded' : ''}`}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  {expandedArticle === article.id && (
                    <div class="help-article-content">
                      <div class="help-article-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatContent(article.content)) }} />
                      {article.relatedSettings && (
                        <button
                          class="help-article-link"
                          onClick={() => route(article.relatedSettings)}
                        >
                          Go to related settings
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* FAQs */}
      <div id="faqs" class="bento-card help-faqs">
        <h2 class="help-faqs-title">Frequently Asked Questions</h2>
        <div class="faq-list">
          {filteredFaqs.map((faq, i) => (
            <div key={i} class="faq-item">
              <button
                class={`faq-question ${expandedFaq === i ? 'expanded' : ''}`}
                onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
              >
                <span>{faq.question}</span>
                <svg
                  class={`faq-chevron ${expandedFaq === i ? 'expanded' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {expandedFaq === i && (
                <div class="faq-answer">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Still need help */}
      <div class="bento-card help-contact">
        <div class="help-contact-content">
          <h3>Still need help?</h3>
          <p>Our support team is happy to assist you with any questions.</p>
        </div>
        <button class="btn btn-primary" onClick={() => {
          const supportBtn = document.querySelector('.support-fab');
          if (supportBtn) supportBtn.click();
        }}>
          Contact Support
        </button>
      </div>
    </div>
  );
}

// Simple markdown-like formatting
function formatContent(content) {
  return content
    .trim()
    .split('\n\n')
    .map(paragraph => {
      // Headers
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        return `<h4>${paragraph.slice(2, -2)}</h4>`;
      }
      // Lists
      if (paragraph.includes('\n- ')) {
        const items = paragraph.split('\n').filter(l => l.startsWith('- ')).map(l => `<li>${l.slice(2)}</li>`).join('');
        return `<ul>${items}</ul>`;
      }
      if (paragraph.includes('\n1. ')) {
        const items = paragraph.split('\n').filter(l => /^\d+\. /.test(l)).map(l => `<li>${l.replace(/^\d+\. /, '')}</li>`).join('');
        return `<ol>${items}</ol>`;
      }
      // Bold
      let html = paragraph.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      return `<p>${html}</p>`;
    })
    .join('');
}
